import React, { Dispatch, SetStateAction, useState } from 'react'
import { Button, ColorType, Link, Text } from '@clear/design-system'
import { useForm } from 'react-hook-form'
import { Form, StepSubTitle, StepTitle } from '@components/layout/Form'
import { verificationCodeSchema } from '@validators/schema/enroll/authSchema'
import { yupResolver } from '@hookform/resolvers/yup'
import OTPInput from './OTPInput'
import { min } from 'lodash'
import { ValidatedContactInfo } from './SelectVerificationMethodForm'

type ContactDetails = Pick<ValidatedContactInfo, 'emailAddress' | 'phoneNumber'>

export type InitiateAuth = {
  otpSessionId: string

  /** form can be trigger with application id flow in which phone and email may not be present */
} & Partial<ContactDetails>

export type ResendAuth = {
  type: 'resend'
  otpSessionId: string
} & ContactDetails

type ContactInfo = InitiateAuth | ResendAuth

export type VerificationCodeFormFields = {
  verificationCode: string
}

export type ValidateVerificationCodeSubmitted =
  Required<VerificationCodeFormFields>

type OTPFailureMessage = string | undefined

export type VerificationCodeFormProps = {
  isLoading?: boolean
  showLogo?: boolean

  otpFailureMessage: OTPFailureMessage
  setOtpFailureMessage?: Dispatch<SetStateAction<OTPFailureMessage>>

  onSubmitCode: (req: VerificationCodeFormFields) => void
  onResendCode: (
    req: Partial<ContactDetails> | Required<ContactDetails>
  ) => void
  contactInfo: InitiateAuth
}

const VerificationCodeForm: React.FC<VerificationCodeFormProps> = ({
  showLogo = false,

  otpFailureMessage,
  setOtpFailureMessage,

  isLoading,

  onResendCode: onResendCode,
  onSubmitCode: onSubmitCode,
  contactInfo,
}) => {
  const [hasCodeBeenResended, sethasCodeBeenResended] = useState(false)
  const { handleSubmit, formState, register, setValue, watch } =
    useForm<VerificationCodeFormFields>({
      resolver: yupResolver(verificationCodeSchema),
      mode: 'onChange',
      defaultValues: {
        verificationCode: ''.padEnd(6, ''),
      },
    })

  const { isValid } = formState
  const otpValue = watch('verificationCode')
  function clearOtpFailureMessage() {
    setOtpFailureMessage?.(undefined)
  }

  async function onFormSubmit(formData: VerificationCodeFormFields) {
    clearOtpFailureMessage()
    onSubmitCode(formData)
  }

  return (
    <>
      <Logo enabled={showLogo} />
      <StepTitle
        data-testid="verification-code-inset-title"
        mt={0}
        textAlign="center"
        color={'blackNeutral' as ColorType}
        fontSize={5}
      >
        Enter Verification Code
      </StepTitle>
      <StepSubTitle
        data-testid="verification-code-helper-text"
        fontSize={2}
        textAlign="center"
      >
        <Status info={contactInfo} />
      </StepSubTitle>
      <Form onSubmit={handleSubmit(onFormSubmit)} mt={5}>
        <OTPInput
          name="verificationCode"
          register={register}
          setValue={setValue}
          watch={watch}
          value={otpValue}
        />
        <ErrorLabel errorMessage={otpFailureMessage} />
        <ResendCode
          info={contactInfo}
          disabled={hasCodeBeenResended}
          onClick={(info) => {
            onResendCode(info)
            clearOtpFailureMessage()
            setValue('verificationCode', '', { shouldValidate: true })
            sethasCodeBeenResended(true)
          }}
        />
        <Button
          mt={5}
          type="submit"
          text="Next"
          id="authv2-verify-otp-btn"
          variant="primary"
          disabled={!isValid || isLoading}
          color="neutralsWhite"
          data-cy="authv2-verify-otp-btn"
        />
      </Form>
    </>
  )
}

const Logo: React.FC<{ enabled: boolean }> = ({ enabled }) =>
  enabled ? (
    <img
      data-testid="auth-inset-precheck-logo"
      className="pb-5 mx-auto"
      src="tsa-pre-logo-auth.svg"
      alt="TSA pre-check with CLEAR logo"
    />
  ) : null

const SemiBoldText = ({ children }) => {
  return <Text fontWeight="600">{children}</Text>
}

const getMaskedEmailComponent = (email?: string) => {
  if (!email) {
    return null
  }

  const [local, domain] = email.split('@')
  if (local.length <= 3) {
    return (
      <SemiBoldText>
        {local[0]}---@{domain}
      </SemiBoldText>
    )
  }
  return (
    <SemiBoldText>
      {local[0]}
      {'-'.repeat(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        min([local.length - 3, 6])!
      )}
      {local.slice(-2)}@{domain}
    </SemiBoldText>
  )
}

const getMaskedPhoneNumberComponent = (phoneNumber?: string) => {
  if (!phoneNumber) {
    return null
  }

  const lastFour = phoneNumber.slice(-4)
  return <SemiBoldText>(---) ---{lastFour}</SemiBoldText>
}

const Status: React.FC<{
  info: Partial<ContactDetails>
}> = ({ info }) => {
  const { emailAddress, phoneNumber } = info
  const maskedEmail = getMaskedEmailComponent(emailAddress)
  const maskedPhone = getMaskedPhoneNumberComponent(phoneNumber)

  if (maskedEmail && maskedPhone) {
    return (
      <>
        We sent a temporary code to {maskedEmail} and {maskedPhone}. Please
        enter the code below.
      </>
    )
  }

  return null
}

const ErrorLabel: React.FC<{ errorMessage?: string }> = ({ errorMessage }) => (
  <Text
    data-testid="verification-code-failure-message"
    mt={3}
    textAlign="center"
    color="utilityDarkErrorRed"
  >
    {errorMessage}
  </Text>
)

const isAbleToResendCode = (
  x: ContactInfo,
  hasCodeBeenResended: boolean
): x is ResendAuth => {
  const isEmailPresent = !!x?.emailAddress
  const isPhonePresent = !!x?.phoneNumber

  const canResend =
    (!isEmailPresent && !isPhonePresent) || (isEmailPresent && isPhonePresent)

  return canResend && !hasCodeBeenResended
}

const ResendCode: React.FC<{
  info: ContactInfo
  disabled: boolean
  onClick: (info: ResendAuth) => void
}> = ({ info, disabled, onClick }) => (
  <>
    {isAbleToResendCode(info, disabled) ? (
      <Link
        data-testid="verification-code-resend-otp-button"
        mt={5}
        id="authv-resend-otp-btn"
        onClick={() => onClick(info)}
        data-cy="authv-resend-otp-btn"
        textAlign="center"
        border="none"
      >
        Resend Code
      </Link>
    ) : (
      <Link
        data-testid="verification-code-resent-button"
        mt={5}
        id="authv-resend-otp-btn"
        data-cy="authv-resend-otp-btn"
        border="none"
        textAlign="center"
      >
        Code has been resent
      </Link>
    )}
  </>
)

export default VerificationCodeForm
